// Canonical group-stage ordering. This is THE source of truth for how teams
// within a World Cup group are ranked — both the web and native clients
// aggregate their own standing rows locally but delegate the ORDERING here.
//
// 2026 FIFA tiebreaker chain (confirmed across ESPN, FOX Sports, and FIFA
// Article 13), applied to teams level on overall POINTS:
//   1. head-to-head points        ┐ "mini-table" among only the tied teams;
//   2. head-to-head goal diff      │ reapplied to any still-tied SUBSET before
//   3. head-to-head goals scored  ┘ falling through (the recursive part)
//   4. overall goal difference
//   5. overall goals scored
//   6. team conduct / fair-play (disciplinary points, fewer = better)
//   7. FIFA world ranking (lower position = better; final fallback — drawing
//      of lots was removed for 2026)
// Team name (alphabetical) is the absolute final stabiliser so the order is
// always total and deterministic.

import type { MatchEventType } from './types';

export interface OrderableTeam {
  id: string;
  name: string;
  /** Pre-tournament FIFA ranking position (1 = best); null when unseeded. */
  fifaRanking: number | null;
}

/**
 * The standing-row fields this module reads. Callers pass their own richer row
 * (live-coloring fields, etc.) — it only has to be assignable to this, and the
 * generic on `orderGroupStandings` preserves the caller's concrete type.
 */
export interface StandingRow {
  team: OrderableTeam;
  pts: number; // overall points
  gf: number; // overall goals for
  ga: number; // overall goals against
  /** Overall fair-play penalty magnitude (>= 0); see disciplinaryPointsForTeamInMatch. */
  disciplinaryPoints: number;
}

/**
 * A single group match reduced to what head-to-head needs. Full-time scores
 * only — the group stage ignores ET/pens.
 */
export interface H2HMatch {
  team1Id: string;
  team2Id: string;
  team1Goals: number;
  team2Goals: number;
}

/**
 * Order a group's rows best-first by the full 2026 tiebreaker chain. Returns a
 * NEW array; never mutates the input. `h2hMatches` is every group match among
 * the passed-in rows (the caller already has them).
 */
export function orderGroupStandings<R extends StandingRow>(
  rows: R[],
  h2hMatches: H2HMatch[],
): R[] {
  // Group by overall points (descending). Teams level on points form a tied
  // block resolved together; everyone else is already separated.
  const byPts = [...rows].sort((a, b) => b.pts - a.pts);

  const ordered: R[] = [];
  let i = 0;
  while (i < byPts.length) {
    let j = i + 1;
    while (j < byPts.length && byPts[j]!.pts === byPts[i]!.pts) j += 1;
    const block = byPts.slice(i, j);
    if (block.length === 1) ordered.push(block[0]!);
    else ordered.push(...resolveTiedSet(block, h2hMatches));
    i = j;
  }
  return ordered;
}

interface MiniStat {
  pts: number;
  gd: number;
  gf: number;
}

function applyMini(stat: MiniStat, gf: number, ga: number): void {
  stat.gf += gf;
  stat.gd += gf - ga;
  if (gf > ga) stat.pts += 3;
  else if (gf === ga) stat.pts += 1;
}

function sameMini(a: MiniStat, b: MiniStat): boolean {
  return a.pts === b.pts && a.gd === b.gd && a.gf === b.gf;
}

/**
 * Build the head-to-head mini-table from only the matches played among exactly
 * the given set of teams (`ids`). Matches touching any team outside the set are
 * ignored — this is what makes the recursive subset re-application work.
 */
function miniTable<R extends StandingRow>(
  teams: R[],
  allH2H: H2HMatch[],
  ids: Set<string>,
): Map<string, MiniStat> {
  const table = new Map<string, MiniStat>();
  for (const t of teams) table.set(t.team.id, { pts: 0, gd: 0, gf: 0 });
  for (const m of allH2H) {
    if (!ids.has(m.team1Id) || !ids.has(m.team2Id)) continue;
    applyMini(table.get(m.team1Id)!, m.team1Goals, m.team2Goals);
    applyMini(table.get(m.team2Id)!, m.team2Goals, m.team1Goals);
  }
  return table;
}

/** Resolve a set of teams level on overall points (steps 1-3, recursively). */
function resolveTiedSet<R extends StandingRow>(tied: R[], allH2H: H2HMatch[]): R[] {
  if (tied.length === 1) return tied;

  const ids = new Set(tied.map((r) => r.team.id));
  const mini = miniTable(tied, allH2H, ids);

  const sorted = [...tied].sort((a, b) => {
    const ma = mini.get(a.team.id)!;
    const mb = mini.get(b.team.id)!;
    if (ma.pts !== mb.pts) return mb.pts - ma.pts;
    if (ma.gd !== mb.gd) return mb.gd - ma.gd;
    if (ma.gf !== mb.gf) return mb.gf - ma.gf;
    return 0;
  });

  // Split into runs equal on the head-to-head key.
  const result: R[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i + 1;
    while (j < sorted.length && sameMini(mini.get(sorted[i]!.team.id)!, mini.get(sorted[j]!.team.id)!)) {
      j += 1;
    }
    const run = sorted.slice(i, j);
    if (run.length === 1) {
      result.push(run[0]!);
    } else if (run.length === tied.length) {
      // Head-to-head separated nothing (full cycle / all draws). Fall through
      // to the overall criteria — never recurse, or this would loop forever.
      result.push(...resolveByOverall(run));
    } else {
      // A strictly smaller still-tied subset: recompute ITS own mini-table
      // (only matches among the subset) and reapply. This is the crux of the
      // 2026 rule — "reapply 1-3 to the teams still concerned".
      result.push(...resolveTiedSet(run, allH2H));
    }
    i = j;
  }
  return result;
}

/**
 * Overall (non-head-to-head) comparison: goal difference → goals scored →
 * conduct → FIFA ranking → name. Serves both as the within-group fall-through
 * (steps 4-7) and — with points prepended — as the cross-group ranking of
 * third-placed teams, who are in different groups and so never met.
 */
function byOverall(a: StandingRow, b: StandingRow): number {
  const aGd = a.gf - a.ga;
  const bGd = b.gf - b.ga;
  if (aGd !== bGd) return bGd - aGd; // overall goal difference
  if (a.gf !== b.gf) return b.gf - a.gf; // overall goals scored
  if (a.disciplinaryPoints !== b.disciplinaryPoints) {
    return a.disciplinaryPoints - b.disciplinaryPoints; // conduct: fewer is better
  }
  // FIFA ranking: lower position is better; unseeded (null) ranks worst.
  const aRank = a.team.fifaRanking ?? Infinity;
  const bRank = b.team.fifaRanking ?? Infinity;
  if (aRank !== bRank) return aRank - bRank;
  return a.team.name.localeCompare(b.team.name); // absolute stabiliser
}

/** Steps 4-7: applied once head-to-head can no longer separate the teams. */
function resolveByOverall<R extends StandingRow>(run: R[]): R[] {
  return [...run].sort(byOverall);
}

/**
 * Rank the third-placed teams from across all groups for the eight "best third
 * place" round-of-32 slots. These teams never played each other (different
 * groups), so head-to-head never applies — FIFA ranks them by points, then the
 * overall criteria (goal difference → goals scored → conduct → FIFA ranking,
 * with team name as the final deterministic stabiliser). Returns a NEW array
 * ordered best-first; callers take the top N.
 */
export function rankThirdPlacedTeams<R extends StandingRow>(thirds: R[]): R[] {
  return [...thirds].sort((a, b) => {
    if (a.pts !== b.pts) return b.pts - a.pts; // points
    return byOverall(a, b); // GD → goals → conduct → FIFA ranking → name
  });
}

/**
 * The set of team ids currently holding the best `slots` (default 8) third-place
 * finishes across the given groups — i.e. the third-placed teams provisionally
 * through to the round of 32. Reads each group's third row (`rows[2]`); World
 * Cup groups always have four teams. The rows already fold in live results, so
 * this set updates as games are played.
 */
export function qualifyingThirdPlaceIds(
  groups: { rows: StandingRow[] }[],
  slots = 8,
): Set<string> {
  const thirds = groups
    .map((g) => g.rows[2])
    .filter((r): r is StandingRow => r !== undefined);
  return new Set(
    rankThirdPlacedTeams(thirds)
      .slice(0, slots)
      .map((r) => r.team.id),
  );
}

// FIFA conduct deductions are per player PER MATCH (cards reset each match):
//   single yellow                → 1
//   two yellows (indirect red)   → 3
//   direct red (no prior yellow) → 4
//   yellow + direct red          → 5
// This app normalises a second yellow to a `red_card` event (see the server's
// livescore-normalise.ts), so a "yellow + red" pair for one player can't be
// told apart from a "yellow + separate direct red"; we score it as the
// dominant case (3, the indirect red). Vanishingly rare and never decisive.
const YELLOW = 1;
const TWO_YELLOWS = 3;
const DIRECT_RED = 4;

/**
 * Fair-play penalty magnitude (>= 0; lower is a cleaner record and ranks
 * higher) for ONE team in ONE match, from that team's card events. The caller
 * sums this across the group's matches into `StandingRow.disciplinaryPoints`.
 */
export function disciplinaryPointsForTeamInMatch(
  events: { type: MatchEventType; player: string | null }[],
): number {
  // Group cards by player so a player's yellow+red counts once (not 1+4).
  // Null player names can't be grouped — score each such card independently.
  const byPlayer = new Map<string, { yellows: number; reds: number }>();
  let total = 0;
  for (const e of events) {
    const isYellow = e.type === 'yellow_card';
    const isRed = e.type === 'red_card';
    if (!isYellow && !isRed) continue;
    if (e.player == null) {
      total += isRed ? DIRECT_RED : YELLOW;
      continue;
    }
    const rec = byPlayer.get(e.player) ?? { yellows: 0, reds: 0 };
    if (isYellow) rec.yellows += 1;
    else rec.reds += 1;
    byPlayer.set(e.player, rec);
  }

  for (const { yellows, reds } of byPlayer.values()) {
    if (reds > 0) {
      // Red with a prior yellow = second-yellow sending-off (3); red with no
      // yellow = straight red (4).
      total += yellows > 0 ? TWO_YELLOWS : DIRECT_RED;
    } else if (yellows >= 2) {
      total += TWO_YELLOWS;
    } else if (yellows === 1) {
      total += YELLOW;
    }
  }
  return total;
}
